import { useEffect, useState } from "react";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { api, asArray, errMsg } from "../../lib/api";
import { ENTITY_TYPES } from "../../data/services";
import { Empty, readFileAsDataUrl } from "./shared";

const EMPTY = { name: "", category: "Stock Broker", logo: "", website: "" };

export const ClientsTab = ({ onChange }) => {
  const [items, setItems] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/clients").then((r) => setItems(asArray(r))).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    try { setF({ ...f, logo: await readFileAsDataUrl(e.target.files?.[0], 500) }); }
    catch (err) { toast.error(err.message); }
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/admin/clients", f); toast.success("Client added"); setF(EMPTY); e.target.reset(); load(); onChange(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name}?`)) return;
    try { await api.delete(`/admin/clients/${c.id}`); toast.success("Removed"); load(); onChange(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-8">
      <form data-testid="client-form" onSubmit={add} className="lg:col-span-2 rounded-2xl bg-white border border-ink/10 p-6 space-y-4 self-start">
        <h2 className="font-display font-bold text-xl">Add a client</h2>
        <div><Label>Client name</Label><Input data-testid="client-name-input" required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
        <div>
          <Label>Category</Label>
          <select data-testid="client-category-select" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm">
            {ENTITY_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <Label>Logo (PNG/SVG/JPG, max 500KB — optional)</Label>
          <input data-testid="client-logo-input" type="file" accept="image/*" onChange={onFile} className="block w-full text-sm mt-1" />
          {f.logo && <img src={f.logo} alt="preview" className="mt-3 h-12 object-contain" />}
        </div>
        <Button data-testid="client-add-button" disabled={busy} className="w-full"><Plus className="w-4 h-4 mr-1" /> {busy ? "Adding…" : "Add client"}</Button>
      </form>
      <div className="lg:col-span-3">
        <h2 className="font-display font-bold text-xl mb-4">Clients on website ({items.length})</h2>
        {!items.length && <Empty testid="admin-clients-empty">No clients added yet.</Empty>}
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((c) => (
            <div key={c.id} data-testid={`admin-client-${c.id}`} className="flex items-center gap-3 rounded-2xl bg-white border border-ink/10 p-4">
              <div className="w-14 h-14 rounded-lg bg-paper-2 grid place-items-center shrink-0 overflow-hidden">
                {c.logo ? <img src={c.logo} alt="" className="max-w-full max-h-full object-contain" /> : <span className="font-display font-bold">{c.name.slice(0, 2).toUpperCase()}</span>}
              </div>
              <div className="flex-1 min-w-0"><p className="font-semibold truncate">{c.name}</p><p className="text-xs text-ink/60">{c.category}</p></div>
              <button data-testid={`admin-client-delete-${c.id}`} onClick={() => remove(c)} className="p-2 rounded-lg hover:bg-red-50 text-red-700"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
